import React, { useEffect, useState } from "react";
import { CheckCircle2, ArrowRight } from "lucide-react";

const BUSINESS_TYPES = [
  "ITAD",
  "Refurbisher",
  "Buyback operator",
  "Repair business",
  "Electronics recycler",
  "Corporate IT",
  "Retail trade-in",
  "Ecommerce reseller",
  "Other",
];

const DEVICE_VOLUMES = ["Under 100", "100–500", "500–2,000", "2,000–10,000", "10,000+", "Not sure"];

const WORKFLOW_INTERESTS = [
  "Erasure",
  "Device assessment",
  "MDM / lock checks",
  "Windows reimaging",
  "Driver capture and reuse",
  "Grading",
  "Buyback workflow",
  "Resale support",
  "Inventory routing",
  "Robot automation",
  "Full asset recovery platform",
];

const AUTOMATION_AREAS = [
  "Automated assessment",
  "Cosmetic grading",
  "MDM checks",
  "Trade-in robot",
  "Reimaging support",
  "Evidence capture",
  "High-volume testing",
  "Not sure yet",
];

const PIPELINE = ["Intake", "Erasure", "Assessment", "Reimage", "Grade", "Resell"];

const fieldCls =
  "w-full rounded-xl border border-line bg-white px-4 py-2.5 text-sm text-brand-plum placeholder:text-faint outline-none transition-colors focus:border-brand-orchid focus:ring-2 focus:ring-brand-orchid/30";
const labelCls = "mb-1.5 block text-sm font-medium text-brand-plum";

const RegisterInterest = () => {
  const [routing, setRouting] = useState("software");
  const [areas, setAreas] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const showRobotFields = routing === "robot" || routing === "both";

  // Robot R&D CTA elsewhere on the page pre-selects this form.
  useEffect(() => {
    const onPreselect = (e) => {
      if (e.detail === "robot") setRouting("robot");
    };
    window.addEventListener("furloop:preselect", onPreselect);
    return () => window.removeEventListener("furloop:preselect", onPreselect);
  }, []);

  const toggleArea = (area) => {
    setAreas((prev) => (prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = Object.fromEntries(new FormData(form).entries());
    data.formName = "Register interest";
    data.interestedIn = routing;
    data.automationAreas = areas;

    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }
      setSubmitted(true);
      window.scrollTo({ top: form.getBoundingClientRect().top + window.scrollY - 120, behavior: "smooth" });
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again or email help@furloop.io.");
    } finally {
      setSubmitting(false);
    }
  };

  const ROUTING_OPTIONS = [
    { value: "software", label: "Software platform" },
    { value: "robot", label: "Robot automation R&D" },
    { value: "both", label: "Both" },
  ];

  return (
    <section id="register" className="relative overflow-hidden bg-brand-bg font-body">
      <div
        className="pointer-events-none absolute -right-24 top-10 h-[26rem] w-[26rem] rounded-full bg-brand-orchid/20 blur-3xl"
        aria-hidden
      />
      <div className="relative mx-auto max-w-3xl px-6 py-20 lg:py-28">
        {/* header */}
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum">
            Register interest
          </span>
          <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-brand-plum sm:text-4xl lg:text-5xl">
            Build a Faster IT Asset Recovery Workflow
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-ink sm:text-lg">
            Connect erasure, assessment, reimaging, grading, reporting, and resale into one workflow — and
            register interest in Furloop's software platform or robot automation R&D.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-2">
            {PIPELINE.map((step, i) => (
              <React.Fragment key={step}>
                <span className="rounded-full border border-line bg-white px-3 py-1 text-xs font-medium text-brand-plum">
                  {step}
                </span>
                {i < PIPELINE.length - 1 && <span className="text-brand-orchid">→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* card */}
        <div className="mt-10 rounded-3xl border border-line bg-white p-6 shadow-xl shadow-brand-plum/5 sm:p-8">
          {submitted ? (
            <div className="flex flex-col items-center py-10 text-center">
              <CheckCircle2 className="h-12 w-12 text-success" />
              <h3 className="mt-4 font-display text-2xl font-semibold text-brand-plum">
                Thank you for registering your interest
              </h3>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-muted">
                We'll review your details and contact you if your workflow is a fit for early access, software
                pilots, or robot automation R&D.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* routing */}
              <div>
                <span className={labelCls}>I'm interested in</span>
                <div className="grid gap-3 sm:grid-cols-3">
                  {ROUTING_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex cursor-pointer items-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
                        routing === opt.value
                          ? "border-brand-magenta bg-brand-wash text-brand-plum"
                          : "border-line bg-white text-muted hover:border-brand-orchid"
                      }`}
                    >
                      <input
                        type="radio"
                        name="interestedIn"
                        value={opt.value}
                        checked={routing === opt.value}
                        onChange={() => setRouting(opt.value)}
                        className="accent-brand-magenta"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* core fields */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls} htmlFor="name">Name</label>
                  <input id="name" name="name" required className={fieldCls} placeholder="Your name" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="company">Company</label>
                  <input id="company" name="company" required className={fieldCls} placeholder="Company name" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="email">Work email</label>
                  <input id="email" name="email" type="email" required className={fieldCls} placeholder="you@company.com" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="phone">Phone number</label>
                  <input id="phone" name="phone" className={fieldCls} placeholder="+1 555 000 0000" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="country">Country</label>
                  <input id="country" name="country" className={fieldCls} placeholder="Country" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="businessType">Business type</label>
                  <select id="businessType" name="businessType" className={fieldCls} defaultValue="">
                    <option value="" disabled>Select…</option>
                    {BUSINESS_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="devicesPerMonth">Devices processed per month</label>
                  <select id="devicesPerMonth" name="devicesPerMonth" className={fieldCls} defaultValue="">
                    <option value="" disabled>Select…</option>
                    {DEVICE_VOLUMES.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="workflowInterest">Main workflow interest</label>
                  <select id="workflowInterest" name="workflowInterest" className={fieldCls} defaultValue="">
                    <option value="" disabled>Select…</option>
                    {WORKFLOW_INTERESTS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelCls} htmlFor="currentTools">Current tools used</label>
                <textarea
                  id="currentTools"
                  name="currentTools"
                  rows={2}
                  className={fieldCls}
                  placeholder="Example: Blancco, Phonecheck, Reusely, NSYS, spreadsheets, in-house tools, manual process."
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="challenge">Biggest processing challenge</label>
                <textarea
                  id="challenge"
                  name="challenge"
                  rows={2}
                  className={fieldCls}
                  placeholder="Example: failed erasure jobs, repeated driver work, MDM checks, grading inconsistency, slow reimaging, manual reporting, disconnected tools."
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="message">Message</label>
                <textarea id="message" name="message" rows={3} className={fieldCls} placeholder="Anything else we should know?" />
              </div>

              {/* conditional robot fields */}
              {showRobotFields && (
                <div className="space-y-5 rounded-2xl border border-brand-orchid/30 bg-brand-wash/50 p-5">
                  <p className="text-sm font-semibold text-brand-plum">Robot automation R&D</p>
                  <div>
                    <span className={labelCls}>Automation areas of interest</span>
                    <div className="flex flex-wrap gap-2">
                      {AUTOMATION_AREAS.map((area) => (
                        <button
                          type="button"
                          key={area}
                          onClick={() => toggleArea(area)}
                          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                            areas.includes(area)
                              ? "border-brand-magenta bg-brand-magenta text-white"
                              : "border-line bg-white text-muted hover:border-brand-orchid"
                          }`}
                        >
                          {area}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="bottlenecks">Current manual bottlenecks</label>
                    <textarea id="bottlenecks" name="bottlenecks" rows={2} className={fieldCls} placeholder="Where does manual handling slow you down?" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="deviceTypes">Device types processed</label>
                    <input id="deviceTypes" name="deviceTypes" className={fieldCls} placeholder="Laptops, desktops, mobile devices…" />
                  </div>
                </div>
              )}

              {error && (
                <p className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-cta-gradient px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#304BEF]/25 transition-transform duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
              >
                {submitting ? "Sending…" : "Submit Interest"}
                {!submitting && (
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default RegisterInterest;
